/**
 * Telling a walkthrough the device made from one the server made.
 *
 * Its own module, and pure, because both halves of the offline path need it
 * and neither may import the other: the capture queue has to know not to
 * upload against an id the server has never heard of, and the local
 * walkthrough store — which is what the queue would otherwise be importing —
 * already imports the queue to hand its captures over.
 */

/**
 * The marker on a device-minted id.
 *
 * Deliberately not a valid server id: those are ULIDs, checked against
 * `/^[0-9A-HJKMNP-TV-Z]{26}$/`, so a local id cannot be mistaken for one
 * anywhere — not in a route, not in a request, and not in the queue's
 * `by-session` index, where a collision would file somebody's photos against
 * a stranger's walkthrough.
 */
export const LOCAL_WALKTHROUGH_PREFIX = 'local-';

/** A new id for a walkthrough starting with no signal. */
export function newLocalWalkthroughId(): string {
  // `crypto.randomUUID` for the same reason `clientCaptureId` uses it: it is
  // available everywhere a camera is, and 42 characters in total leaves room
  // inside the server's 64-character limit for the id it dedupes on.
  return `${LOCAL_WALKTHROUGH_PREFIX}${crypto.randomUUID()}`;
}

/** Whether this walkthrough exists only on this device so far. */
export function isLocalWalkthroughId(id: string): boolean {
  return id.startsWith(LOCAL_WALKTHROUGH_PREFIX);
}

/**
 * Local ids the server has since taken over, and what it calls them now.
 *
 * This exists to close a window with a capture in it. Handing a walkthrough
 * over re-keys the captures that exist at that instant — but the walkthrough
 * screen is still open, still says `local-…`, and the cleaner is still taking
 * photos. One taken between the re-key and the screen noticing would be
 * written under an id that is no longer anybody's: the local record is marked
 * adopted, so nothing lists it, and the drain now runs against the server's
 * id, so nothing sends it. The walkthrough finishes and is priced without a
 * room nobody can find again.
 *
 * So the map is set FIRST, before a single row is moved, and every write and
 * every drain resolves through it. In a single-threaded runtime that ordering
 * is the guarantee: after the map is set, no new capture can land under the
 * old id, and everything already under it is exactly what the re-key sweeps.
 *
 * Memory only, and that is enough. It is read by the queue while the tab that
 * did the handover is alive, and a later reload has nothing to resolve — the
 * rows are already under the server's id.
 */
const adopted = new Map<string, string>();

/** Records that `localId` is now the server's `serverId`. */
export function noteAdoption(localId: string, serverId: string): void {
  adopted.set(localId, serverId);
}

/** The id this walkthrough actually goes to the server under, right now. */
export function resolveWalkthroughId(id: string): string {
  return adopted.get(id) ?? id;
}
